/**
 * Feeds Management Page
 *
 * Allows users to subscribe to RSS/Atom feeds and manage existing
 * subscriptions. Supports single URL input and OPML file import.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getFeeds, createFeed, deleteFeed, retryFeed } from '../services/feeds';
import PageLoading from '../components/PageLoading';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import { useOnline } from '../hooks/useOnline';
import { useConfirm } from '../hooks/useConfirm';
import { formatRelativeDate } from '../utils/dateUtils';
import { setTitle } from '../utils/environment';

const Feeds = () => {
  const [feeds, setFeeds] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add feed form state
  const [inputText, setInputText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  // In-flight delete marker (one feed at a time)
  const [deletingId, setDeletingId] = useState(null);

  const online = useOnline();
  const confirm = useConfirm();

  const fetchFeeds = useCallback(async () => {
    try {
      const data = await getFeeds();
      setFeeds(data.feeds || []);
      setTotal(data.total || 0);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    setTitle('Feeds · Read It Never');
    fetchFeeds();
  }, [fetchFeeds]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputText.trim() || isSubmitting) return;

    if (!online) {
      setSubmitResult({ type: 'error', message: 'Connect to internet to add a feed' });
      return;
    }

    setIsSubmitting(true);
    setSubmitResult(null);

    try {
      const result = await createFeed(inputText.trim());
      setInputText('');

      // Detect if this was an OPML import (has total_found field)
      if (result.total_found !== undefined) {
        setSubmitResult({
          type: 'opml',
          added: result.added,
          duplicates: result.duplicates,
          errors: result.errors,
          total: result.total_found,
        });
      } else {
        setSubmitResult({
          type: 'single',
          title: result.title || result.feed_url,
        });
      }

      // Refresh the feed list
      await fetchFeeds();
    } catch (err) {
      setSubmitResult({ type: 'error', message: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (feed) => {
    if (!(await confirm({
      title: 'Delete this feed?',
      message: feed.title || feed.feed_url,
      danger: true,
      confirmLabel: 'Delete',
    }))) return;

    setDeletingId(feed.id);
    try {
      await deleteFeed(feed.id);
      setFeeds(prev => prev.filter(f => f.id !== feed.id));
      setTotal(prev => prev - 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleRetry = async (feedId) => {
    try {
      const updated = await retryFeed(feedId);
      setFeeds(prev => prev.map(f => f.id === feedId ? updated : f));
    } catch (err) {
      setError(err.message);
    }
  };

  const getStatusBadge = (feed) => {
    switch (feed.status) {
      case 'active':
        return <span className="badge-fun-mint text-xs">Active</span>;
      case 'error':
        return <span className="badge-fun-coral text-xs">Error</span>;
      case 'paused':
        return <span className="badge-fun-lemon text-xs">Paused</span>;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <PageLoading message="Loading feeds..." />
    );
  }

  return (
    <div className="min-h-screen bg-clay-bg">
      <PageHeader
        title="RSS Feeds"
        subtitle={`${total} ${total === 1 ? 'subscription' : 'subscriptions'}`}
        backTo="/dashboard"
        backLabel="Back to dashboard"
      />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-8">
        {/* Add Feed Form */}
        <div className="card-clay mb-8">
          <h2 className="font-display text-lg font-semibold text-heading mb-4">Add Feed</h2>
          <form onSubmit={handleSubmit}>
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Paste an RSS feed URL or OPML content..."
              rows={3}
              className="w-full px-4 py-3 border-2 border-clay-border rounded-clay bg-surface text-body-text placeholder-[color:var(--color-text-tertiary)] focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200 resize-y text-sm"
            />
            <div className="flex items-center justify-between mt-3">
              <p className="text-xs text-secondary-text">
                Supports RSS/Atom URLs and OPML import
              </p>
              <button
                type="submit"
                disabled={!inputText.trim() || isSubmitting || !online}
                title={!online ? 'Connect to internet to add a feed' : undefined}
                className="btn-clay flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner-fun-sm mr-2"></div>
                    Adding...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Feed
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Submit Result */}
          {submitResult && (
            <div className={`mt-4 ${
              submitResult.type === 'error' ? 'notice-clay-error' : 'notice-clay-success'
            }`}>
              {submitResult.type === 'error' ? (
                <p>{submitResult.message}</p>
              ) : submitResult.type === 'opml' ? (
                <div>
                  <p className="font-medium mb-1">OPML Import Complete</p>
                  <p>Found {submitResult.total} feeds: {submitResult.added} added, {submitResult.duplicates} duplicates, {submitResult.errors} errors</p>
                </div>
              ) : (
                <p>
                  Feed added: {submitResult.title}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Error Banner */}
        {error && (
          <div className="notice-clay-error mb-6">
            <div className="flex items-center justify-between">
              <p>{error}</p>
              <button onClick={() => setError(null)} className="text-rose-400 hover:text-rose-600">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Feed List */}
        {feeds.length === 0 ? (
          <EmptyState
            icon={
              <svg className="w-16 h-16 text-tertiary-text mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 5c7.18 0 13 5.82 13 13M6 11a7 7 0 017 7m-6 0a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            }
            title="No Feeds Yet"
            description="Add your first RSS feed above to start receiving articles automatically."
          />
        ) : (
          <div className="space-y-3">
            {feeds.map((feed) => (
              <div key={feed.id} className="card-clay-sm hover:shadow-clay-hover transition-all duration-200">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="font-semibold text-heading break-words min-w-0">
                        {feed.title || feed.feed_url}
                      </h3>
                      {getStatusBadge(feed)}
                    </div>
                    {feed.title && (
                      <p className="text-xs text-secondary-text truncate mb-1">{feed.feed_url}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-secondary-text">
                      <span>Checked: {formatRelativeDate(feed.last_checked_at) || 'Never'}</span>
                      <span>{feed.check_count} polls</span>
                    </div>
                    {feed.last_error && (feed.status === 'error' || feed.status === 'paused') && (
                      <p className="text-xs text-rose-600 mt-2 line-clamp-2">{feed.last_error}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0 self-end sm:self-start">
                    {(feed.status === 'error' || feed.status === 'paused') && (
                      <button
                        onClick={() => handleRetry(feed.id)}
                        className="btn-clay-secondary btn-clay-sm text-xs"
                        title="Retry"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </button>
                    )}

                    <button
                      onClick={() => handleDelete(feed)}
                      disabled={deletingId === feed.id}
                      className="btn-clay-danger btn-clay-sm text-xs"
                      title="Delete"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Feeds;
